import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Eye, UploadCloud, Archive, ClipboardList, Lock, Pencil } from "lucide-react";
import { toast } from "sonner";
import { ChecklistBuilder } from "@/components/forms/ChecklistBuilder";
import { ChecklistFill } from "@/components/forms/ChecklistFill";
import { ChecklistEmailTemplate } from "@/components/forms/ChecklistEmailTemplate";
import { openPdf } from "@/components/forms/ChecklistEmailDialog";
import { SentEmailsLog } from "@/components/forms/SentEmailsLog";
import { Mail, FileText, Download } from "lucide-react";

const STATUS = { draft: ["Draft", "bg-slate-100 text-slate-600"], published: ["Published", "bg-emerald-50 text-emerald-700 border-emerald-200"], archived: ["Archived", "bg-amber-50 text-amber-700 border-amber-200"] };

export default function Forms() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [jobTypes, setJobTypes] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [initial, setInitial] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [tab, setTab] = useState("checklists");

  const load = () => client.get("/forms/templates").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    client.get("/job-types").then((r) => setJobTypes(Object.fromEntries((r.data || []).map((j) => [j.value, j.label])))).catch(() => {});
  }, []);
  if (!can("checklist.manage")) return <Navigate to="/" replace />;

  const openNew = () => { setEditing(null); setInitial(null); setOpen(true); };
  const openEdit = (tpl) => {
    setEditing(tpl.id);
    setInitial({ name: tpl.name, scope: tpl.scope || "job", jobTypes: tpl.jobTypes?.length ? tpl.jobTypes : tpl.jobType ? [tpl.jobType] : [], enforce: !!tpl.enforce, instructions: tpl.instructions || "", sections: tpl.sections || [] });
    setOpen(true);
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const remove = (tpl) => { if (window.confirm(`Delete "${tpl.name}" permanently?`)) act(() => client.delete(`/forms/templates/${tpl.id}`), "Checklist deleted"); };
  const list = rows.filter((r) => showArchived || r.status !== "archived").sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="space-y-5" data-testid="forms-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-brand-ink">Forms & Checklists</h2>
          <p className="text-sm text-slate-500">Make a checklist for each type of job. Published checklists are added to new jobs of the chosen types.</p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-600"><Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="forms-show-archived" />Show archived</label>
          <Button variant="outline" data-testid="email-template-btn" onClick={() => setTplOpen(true)}><Mail className="h-4 w-4 mr-2" />Client email template</Button>
          <Button data-testid="form-new-btn" onClick={openNew} className="bg-brand hover:bg-brand-dark"><Plus className="h-4 w-4 mr-2" />New checklist</Button>
        </div>
      </div>
      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="forms-tabs">
        {[["checklists", "Checklists"], ["sent", "Sent emails"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`forms-tab-${k}`}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${tab === k ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>
      {tab === "sent" ? <SentEmailsLog /> : list.length === 0 ? (
        <Card className="p-10 text-center text-slate-500 border-slate-200" data-testid="forms-empty"><ClipboardList className="h-8 w-8 mx-auto mb-2 text-slate-300" />No checklists yet. Click <b>New checklist</b> to make your first one.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((tpl) => {
            const [label, cls] = STATUS[tpl.status] || STATUS.draft;
            const types = (tpl.jobTypes?.length ? tpl.jobTypes : tpl.jobType ? [tpl.jobType] : []).map((v) => jobTypes[v] || v);
            const fields = (tpl.sections || []).reduce((n, s) => n + (s.items || []).length, 0);
            return (
              <Card key={tpl.id} data-testid={`form-template-${tpl.id}`} className="p-4 border-slate-200 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{tpl.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{fields} field{fields === 1 ? "" : "s"}</p>
                  </div>
                  <Badge variant="outline" className={cls}>{label}</Badge>
                </div>
                <p className="text-xs text-slate-500">{types.length ? `Auto-added to: ${types.join(", ")}` : "Not auto-added (pick it on a job)"}</p>
                <div className="flex flex-wrap gap-1.5">
                  <Button size="sm" variant="outline" data-testid={`form-preview-${tpl.id}`} onClick={() => setPreview({ id: null, name: tpl.name, sectionsSnapshot: tpl.sections, enforce: tpl.enforce, answers: {} })}><Eye className="h-3.5 w-3.5 mr-1" />Preview</Button>
                  <Button size="sm" variant="outline" title="Preview as PDF" data-testid={`form-pdf-${tpl.id}`} onClick={() => openPdf(`/forms/templates/${tpl.id}/preview.pdf`)}><FileText className="h-3.5 w-3.5 mr-1" />PDF</Button>
                  <Button size="sm" variant="ghost" title="Download PDF" data-testid={`form-pdf-download-${tpl.id}`} onClick={() => openPdf(`/forms/templates/${tpl.id}/preview.pdf`, `${tpl.name}.pdf`)}><Download className="h-3.5 w-3.5" /></Button>
                  {tpl.status !== "archived" && <Button size="sm" variant="outline" data-testid={`form-edit-${tpl.id}`} onClick={() => openEdit(tpl)}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>}
                  {tpl.status !== "archived" && <Button size="sm" variant="outline" data-testid={`form-publish-${tpl.id}`} onClick={() => act(() => client.post(`/forms/templates/${tpl.id}/publish`), "Published")} className="text-emerald-700 border-emerald-200"><UploadCloud className="h-3.5 w-3.5 mr-1" />Publish</Button>}
                  {tpl.status !== "archived" && <Button size="sm" variant="ghost" title="Archive" data-testid={`form-archive-${tpl.id}`} onClick={() => act(() => client.post(`/forms/templates/${tpl.id}/archive`), "Archived")} className="text-amber-700"><Archive className="h-3.5 w-3.5" /></Button>}
                  <Button size="sm" variant="ghost" title="Delete" data-testid={`form-delete-${tpl.id}`} onClick={() => remove(tpl)} className="text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      <ChecklistBuilder open={open} onOpenChange={setOpen} editingId={editing} initial={initial} onSaved={load} />
      <ChecklistFill inst={preview} onOpenChange={(o) => !o && setPreview(null)} preview />
      <ChecklistEmailTemplate open={tplOpen} onOpenChange={setTplOpen} />
    </div>
  );
}
